
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Test, Question } from "@/types/test";
import { useToast } from "@/hooks/use-toast";

export const useTestData = (testId: string | undefined) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ["test", testId],
    queryFn: async () => {
      if (!testId) {
        console.error("No test ID provided to useTestData");
        throw new Error("No test ID provided");
      }

      console.log("Fetching test with ID:", testId);

      const { data: test, error } = await supabase
        .from("tests")
        .select("*")
        .eq("id", testId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching test:", error);
        toast({
          title: "Error Loading Test",
          description: "Failed to load the test. Please try again.",
          variant: "destructive",
        });
        throw error;
      }

      if (!test) {
        console.error(`Test with ID ${testId} not found`);
        toast({
          title: "Test Not Found",
          description: "The requested test could not be found.",
          variant: "destructive",
        });
        throw new Error("Test not found");
      }

      console.log("Raw test data:", test);

      const rawQuestions = test.questions || [];
      if (!Array.isArray(rawQuestions) || rawQuestions.length === 0) {
        console.error("Test has no questions:", test);
        toast({
          title: "Invalid Test",
          description: "This test has no questions.",
          variant: "destructive",
        });
        throw new Error("Test has no questions");
      }

      // Find the specific question about the snake and bear, if it exists
      const animalQuestion = rawQuestions.find((q: any) => 
        q.text && 
        typeof q.text === 'string' && 
        q.text.includes("snake") && 
        q.text.includes("bear") && 
        q.text.includes("ground")
      );
      
      // If the question exists, update it with the new text
      if (animalQuestion && typeof animalQuestion === 'object') {
        animalQuestion.text = "Show your child a picture of a snake and a bear. Ask, \"Which one walks on the ground?\" Do they answer correctly?";
      }

      const parsedQuestions = rawQuestions.map((q: any): Question => ({
        id: q.id,
        text: q.text,
        options: Array.isArray(q.options) ? q.options.map(String) : [],
        correctAnswer: q.correctAnswer,
        isInputField: q.isInputField || false,
        inputType: q.inputType,
        imageUrl: q.imageUrl,
        imageUrls: Array.isArray(q.imageUrls) ? q.imageUrls.map(String) : undefined
      }));

      // Ensure question_images is the correct type
      let questionImages: Record<string, string[]> = {};
      if (test.question_images && typeof test.question_images === 'object') {
        questionImages = Object.entries(test.question_images).reduce((acc, [key, value]) => {
          if (Array.isArray(value)) {
            acc[key] = value.map(String);
          }
          return acc;
        }, {} as Record<string, string[]>);
      }

      const processedTest: Test = {
        id: test.id,
        title: test.title,
        description: test.description,
        grade_level: test.grade_level,
        category: test.category,
        difficulty: test.difficulty,
        created_at: test.created_at,
        updated_at: test.updated_at,
        questions: parsedQuestions,
        question_images: questionImages
      };

      console.log("Processed test data:", processedTest);
      return processedTest;
    },
    enabled: !!testId,
    retry: 1,
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });
};
